import { Hotel } from "typings/microserviceModels/hotels-flex-module";
import * as React from "react";
import { UitkRating, UitkRatingIcon } from "uitk-react-rating";
import { getHotelLinkRel } from "components/flexComponents/Hotels/components/HotelCard";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { LocalizedComponentClass, withLocalization, LocalizedComponentProps } from "bernie-l10n";
import { HotelAddressDistanceToPoiAndValueProposition } from "components/flexComponents/Hotels/components/HotelAddressDistanceToPoiAndValueProposition";
import { HotelAddressDistanceToPoiAndValuePropositionCOMET } from "components/flexComponents/Hotels/components/HotelAddressDistanceToPoiAndValuePropositionCOMET";
import { getAddress } from "components/flexComponents/Hotels/utils/getAddress";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { ratingIcon } from "components/utility/RatingUtil";

/**
 * CardContent which contains the hotel name, address and overall star rating
 */

export interface HotelNameRatingAndAddressProps extends LocalizedComponentProps {
  hotel: Hotel;
  locale: string;
  moduleName?: string;
  isViewCarousel?: boolean;
  context?: ExtendedContextStore;
  isTravelersLoved?: boolean;
  locationName: string;
  shouldUseImperialDistanceUnits?: boolean;
  addressDistanceView?: string;
  showDistance?: boolean;
  showAddress?: boolean;
}

const HotelNameRatingAndAddressComponent = (props: HotelNameRatingAndAddressProps) => {
  const {
    hotel,
    locale,
    moduleName,
    isViewCarousel,
    context,
    isTravelersLoved,
    locationName,
    shouldUseImperialDistanceUnits,
    addressDistanceView,
    showDistance,
    showAddress,
    l10n,
  } = props;
  if (!hotel) {
    return null;
  }
  const { hotelStarRating, callToActionHotelNameUrl, distancePoi } = hotel;
  const hotelRatingIcon = ratingIcon(locale);
  const hotelLink = callToActionHotelNameUrl ? (
    <TrackedLink
      data-testid={`hotelname-link-${hotel.hotelId}`}
      moduleName={moduleName || "hotels"}
      className="hotelNameLink"
      rel={getHotelLinkRel(callToActionHotelNameUrl)}
      rfrr={`hotelName-${hotel.hotelId}`}
      href={callToActionHotelNameUrl}
    >
      <HotelName hotelName={hotel.hotelName} />
    </TrackedLink>
  ) : (
    <HotelName hotelName={hotel.hotelName} />
  );

  const getDistanceToPOI = () => {
    // The choice of unit should really be done in the back-end or at least the back-end should provide info
    // about which unit to choose...
    return (
      locationName &&
      distancePoi &&
      (shouldUseImperialDistanceUnits
        ? l10n.formatText("hotels.distanceToPoi.miles", distancePoi.miles, locationName)
        : l10n.formatText("hotels.distanceToPoi.kilometers", distancePoi.kilometers, locationName))
    );
  };

  return (
    <>
      {!isViewCarousel && <div className="truncate uitk-type-600 uitk-type-bold">{hotelLink}</div>}
      {isTravelersLoved && <div className="uitk-type-600 uitk-type-bold">{hotelLink}</div>}

      <MemoizedUitkRating
        hotelRatingIcon={hotelRatingIcon}
        hotelStarRating={hotelStarRating}
        brand={context?.site?.brand}
      />

      {addressDistanceView === "COMET" ? (
        <HotelAddressDistanceToPoiAndValuePropositionCOMET
          hotel={hotel}
          address={getAddress(hotel)}
          distance={getDistanceToPOI()}
          showDistance={showDistance && !!distancePoi}
          showAddress={showAddress}
          isViewCarousel={isViewCarousel}
        />
      ) : (
        <HotelAddressDistanceToPoiAndValueProposition
          hotel={hotel}
          address={getAddress(hotel)}
          distance={getDistanceToPOI()}
          showDistance={!!distancePoi}
          isViewCarousel={isViewCarousel}
        />
      )}
    </>
  );
};

export const HotelNameRatingAndAddress: LocalizedComponentClass<HotelNameRatingAndAddressProps> = withLocalization(
  HotelNameRatingAndAddressComponent
);

interface MemoizedUitkRatingProps {
  hotelRatingIcon: UitkRatingIcon;
  hotelStarRating: string;
  brand?: string;
}

const MemoizedUitkRating = React.memo((props: MemoizedUitkRatingProps) => (
  <UitkRating
    overlay={false}
    className={`uitk-type-100 ${props.brand}`}
    icon={props.hotelRatingIcon}
    rating={props.hotelStarRating}
  />
));

export const HotelName = ({ hotelName }: { hotelName: string }) => (
  <span className="uitk-type-heading-500" data-testid="hotel-name">
    {hotelName}
  </span>
);
